import React, { useEffect, useState } from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { useOpenCompetencyDrawer } from 'components/Develop/helpers/useOpenCompetencyDrawer';
import { useSelector } from 'react-redux';
import { CompetenciesMySummaryRow } from 'screens/home/summary/components/Cards/LevelUp/CompetenciesMySummaryCard';
import { useMySummaryCompetency } from 'screens/home/summary/components/Cards/LevelUp/hooks';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';
import { useOneOnOneInstanceDetails } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { LevelUpIconDualTone } from 'assets/assetsV1/iconComponents';
import NoFeedbackImage from 'assets/assetsV1/illustrations/noFeedback.svg';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../common/components/SidebarCardTemplate';

const CompetencySection = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const {
    receiverEmployee: { id: employeeId, displayName },
    relationType,
  } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const isLoggedInEmployeeTheReceiver = loggedInEmployeeId === employeeId;

  const isLoggedInEmployeeManager = relationType === 'MANAGER' || relationType === 'SKIP_MANAGER';

  const { openCompetencyPanel } = useOpenCompetencyDrawer();

  const { openLevelUpSidePanel } = useLevelUpSidePanelContext();
  //   const {
  //     //   @ts-ignore
  //     state: { checkInInstanceId, checkInParentId, filterEmpIds }
  //   } = useLevelUpProfilePageContext();

  const { data, isLoading } = useMySummaryCompetency({
    limit: 50,
    offset: 0,
    employeeId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { isLevelUpEnabled } = data ?? {};

  const jobCompetencies = [
    {
      competencies: data?.competencies,
    },
  ];

  const hasTargetRole = jobCompetencies?.length > 1;
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(0); // <- 0 in current role and 1 is target role

  useEffect(() => {
    setSelectedRoleIndex(hasTargetRole ? 1 : 0);
  }, [hasTargetRole]);

  const handleOpenLevelUpSidePanel = ({ competencyId, levelUpId, levelUpCompetencyId }) => {
    // @ts-ignore
    openLevelUpSidePanel({
      reporteeId: employeeId,
      competencyId,
      levelUpId,
      levelUpCompetencyId,
      isLoggedInEmployeeManager,
    });
  };

  const openNonLevelUpCompetencySidePanel = ({ competencyId, name }) => {
    openCompetencyPanel({
      tagId: competencyId,
      tagTitle: name,
      employeeId,
    });
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!jobCompetencies) {
    return (
      <SidebarCardTemplate label="Competencies" Icon={LevelUpIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoFeedbackImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>
              {isLoggedInEmployeeTheReceiver
                ? 'There are no competencies added for your role'
                : `There were no competencies added for this role`}
            </TextBodyMedium>
            <TextBodyMedium color="text.g2">
              {isLoggedInEmployeeTheReceiver
                ? 'You role does not have any competencies setup on mesh right now. Contact your admin to learn more.'
                : `There are no competencies setup on mesh right now for ${displayName}'s role. Contact your admin to learn more.`}
            </TextBodyMedium>
          </Flex>
        </Flex>
      </SidebarCardTemplate>
    );
  }

  if (!jobCompetencies[selectedRoleIndex]?.competencies?.length) {
    return (
      <SidebarCardTemplate label="Competencies" Icon={LevelUpIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoFeedbackImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>You haven't created any competencies yet</TextBodyMedium>
            <TextBodyMedium color="text.g2">
              The best way to have a garden full of roses is to plant them! Take initiative and get started today!
            </TextBodyMedium>
          </Flex>
        </Flex>
      </SidebarCardTemplate>
    );
  }

  return (
    <SidebarCardTemplate label="Competencies" Icon={LevelUpIconDualTone}>
      {jobCompetencies[selectedRoleIndex]?.competencies?.map(
        (
          { levelUpCompetencyId, competencyId, name, targetPeriodStart, progressPercentage, progress, progressDetails },
          index
        ) => (
          <Box
            sx={{
              borderRadius: '8px',
              background: 'white',
              margin: '4px 0px',
              '.highlightTextOnHover': {
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <CompetenciesMySummaryRow
              levelUpCompetencyId={levelUpCompetencyId}
              competencyId={competencyId}
              name={name}
              targetPeriodStart={targetPeriodStart}
              progressPercentage={progressPercentage}
              progress={progress}
              key={`${competencyId}-${index}-competency-row`}
              handleOpenLevelUpSidePanel={handleOpenLevelUpSidePanel}
              levelUpId={jobCompetencies[selectedRoleIndex]?.levelUpId}
              showDivider
              isLevelUpEnabled={isLevelUpEnabled}
              // @ts-ignore
              openNonLevelUpCompetencySidePanel={openNonLevelUpCompetencySidePanel}
              isOneOnOneSidebar
              source="Module Data in 1:1s"
              progressDetails={progressDetails}
            />
          </Box>
        )
      )}
    </SidebarCardTemplate>
  );
};

export { CompetencySection };
